// Cross-browser file drag-and-drop helpers.
//
// Firefox is stricter than Chrome about the HTML drag-and-drop contract:
//   • a valid drop target must cancel BOTH dragenter and dragover (Chrome is
//     lenient and accepts dragover alone) — every drop zone must therefore
//     attach an onDragEnter that calls preventDefault();
//   • some drag sources populate `dataTransfer.items` but leave
//     `dataTransfer.files` empty — read through the items API as a fallback;
//   • a file dropped OUTSIDE a drop zone replaces the SPA with the file
//     (losing all page state) — useWindowDropGuard() cancels that default.

import { useEffect } from 'react'
import type { DragEvent } from 'react'

/** The dropped files of a drop event, with the items-API fallback. */
export function getDroppedFiles(e: DragEvent): File[] {
  const dt = e.dataTransfer
  if (!dt) return []
  if (dt.files && dt.files.length > 0) return Array.from(dt.files)
  return Array.from(dt.items ?? [])
    .filter(it => it.kind === 'file')
    .map(it => it.getAsFile())
    .filter((f): f is File => f !== null)
}

/**
 * While mounted, stop the browser from navigating away when a FILE drag is
 * dropped outside a drop zone. Zone handlers run first (events bubble), so
 * this only cancels the default; non-file drags (text into inputs, the
 * MegaTabelle school-assignment drag) are untouched.
 */
export function useWindowDropGuard(): void {
  useEffect(() => {
    const prevent = (e: globalThis.DragEvent) => {
      if (e.dataTransfer?.types?.includes('Files')) e.preventDefault()
    }
    window.addEventListener('dragover', prevent)
    window.addEventListener('drop', prevent)
    return () => {
      window.removeEventListener('dragover', prevent)
      window.removeEventListener('drop', prevent)
    }
  }, [])
}
